import '.bh{position:fixed;}.ssr-plugin'
import '.dq{top:0px;}.ssr-plugin'
import '.dp{left:0px;}.ssr-plugin'
import '.h{width:100%;}.ssr-plugin'
import '.o{height:100%;}.ssr-plugin'
import '.w{pointer-events:none;}.ssr-plugin'
import '.jd{z-index:9999;}.ssr-plugin'
import '.a9{cursor:pointer;}.ssr-plugin'
import { Avatar } from '../Avatar/index.js'
import { Menu } from '../Menu/index.js'
import { Text } from '../Text/index.js'
import { useUsers } from '../../hooks/useUsers.js'
import { styled } from 'inlines'
import { Stack } from '../Stack/index.js'
import { useFollowMe } from '../../hooks/useFollowMe.js'
import { useEffect, useMemo, useState } from 'react'
import { capitalize } from '../../utils/text.js'
import { useToast } from '../Toast/index.js'
import { colors } from '../../tokens/colors.js'
import { Button } from '../Button/index.js'
import { Icon } from '../Icon/index.js'
import { UserAvatar, userAvatarColors } from '../UserMenu/index.js'
import { useTheme } from '../../hooks/useTheme.js'
import { useLocation } from '../../hooks/useCmsParams.js'
import { radius } from '../../utils/radius.js'

export const FollowMe = ({
  mode = 'full',
}: {
  mode: 'notification' | 'avatars' | 'full'
}) => {
  const { me, all } = useUsers()
  const [followMe, setFollowMe] = useFollowMe()
  const [location, setLocation] = useLocation()
  const [followers, setFollowers] = useState<string[]>([])
  const [following, setFollowing] = useState<string>('')
  const [availableUsers, setAvailableUsers] = useState<any[]>([])
  const [privateMode, setPrivateMode] = useState<boolean>(false)
  const { theme } = useTheme()
  const toast = useToast()

  const sortUsers = (users: any[], myId: number) => {
    if (!users) return

    return [...users]
      ?.filter(({ followMe }) => !followMe?.privateMode)
      .sort((a, b) => {
        if (
          !Array.isArray(a.followMe?.followers) ||
          !Array.isArray(b.followMe?.followers)
        ) {
          return 0
        }

        const hasA = a.followMe?.followers?.includes(myId)
        const hasB = b.followMe?.followers?.includes(myId)

        if (hasA && !hasB) return -1
        if (!hasA && hasB) return 1
        return 0
      })
  }

  const sortedAllUsers = useMemo<any[]>(
    () => sortUsers(all, me?.id),
    [all, me?.id],
  )

  useEffect(() => {
    if (!me?.id || !sortedAllUsers?.length) {
      return
    }

    setAvailableUsers(sortedAllUsers)
  }, [me?.id, all, following, followMe?.following?.id])

  useEffect(() => {
    if (Array.isArray(followMe?.followers) && all?.length) {
      if (followMe?.followers?.length === 0) {
        setFollowers([])
        return
      }

      setFollowers((prev) => {
        const followers = all
          .filter(({ id }) => followMe?.followers?.includes(id))
          .map(({ name }) => capitalize(name))

        if (
          followers.length === prev.length &&
          followers.every((follower) => prev.includes(follower))
        ) {
          return prev
        }

        return followers
      })
    }
  }, [all, followMe?.followers])

  useEffect(() => {
    if (followMe?.following?.id !== undefined) {
      setFollowing(capitalize(followMe?.following?.name) || '')
    } else {
      setFollowing(null)
    }
  }, [followMe?.following?.id])

  useEffect(() => {
    if (followMe?.privateMode !== undefined) {
      setPrivateMode(followMe?.privateMode)
    }
  }, [followMe?.privateMode])

  useEffect(() => {
    if (followers.length) {
      let followersMessage = ''

      if (followers.length === 1) {
        followersMessage = `${formatFollowers(followers)} is following you`
      } else if (followers.length <= 4) {
        followersMessage = `${formatFollowers(followers)} are following you`
      } else {
        followersMessage = `${formatFollowers(followers)} +${followers.length - 4} are following you`
      }

      toast(followersMessage, {
        icon: 'show',
        secondaryButtonLabel: 'Stop',
        onSecondaryClick: async () => setFollowMe({ followers: [] }),
      })
    }
  }, [followers])

  useEffect(() => {
    if (
      followMe?.following?.id !== undefined &&
      followMe?.following?.id !== null &&
      followMe?.following?.location
    ) {
      setLocation(followMe?.following?.location, true)
    }
  }, [followMe?.location, followMe?.following?.location])

  useEffect(() => {
    if (
      followMe?.following?.id === undefined ||
      followMe?.following?.id === null
    ) {
      setFollowMe({ location })
    }
  }, [location])

  const formatFollowers = (arr: any[]) => {
    const sliced = arr.slice(0, 4)

    if (sliced.length === 0) return ''
    if (sliced.length === 1) return sliced[0]
    if (sliced.length === 2) return `${sliced[0]} and ${sliced[1]}`

    const allButLast = sliced.slice(0, -1)
    const last = sliced[sliced.length - 1]

    return `${allButLast.join(', ')}, and ${last}`
  }

  const handleFollowMe = (following: number | null, color: string | null) => {
    if (privateMode) {
      return toast(`You're in Private Mode`, {
        description: 'Disable the private mode to follow someone else',
        icon: 'error-filled',
        color: 'red',
      })
    }

    if (
      following === followMe?.following?.id &&
      color === followMe?.following?.color
    ) {
      following = null
      color = null
    }

    setFollowMe({ following: { id: following, color } })
  }

  useEffect(() => {
    const handleClick = () => {
      if (followMe?.following?.id !== null) {
        setFollowMe({ following: { id: null, color: null } })
      }
    }

    window.addEventListener('click', handleClick)

    return () => {
      window.removeEventListener('click', handleClick)
    }
  }, [followMe?.following?.id])

  if (!me || !all || me.role !== 'admin') {
    return null
  }

  const NotifyBadge = ({
    text,
    color,
    type,
    onClick,
  }: {
    text: string
    color?: string
    type: 'following' | 'followed'
    onClick: () => void
  }) => {
    let settings: {
      background: string
      text: string
      button: string
      tooltip: string
      iconColor: string
    }

    if (type === 'following') {
      settings = {
        background: color,
        text: 'white100',
        button: 'white',
        tooltip: 'Stop following',
        iconColor: 'white100',
      }
    } else {
      settings = {
        background: colors.neutral20Adjusted,
        text: 'neutral100',
        button: 'neutral',
        tooltip: 'Stop being followed',
        iconColor: theme === 'dark' ? 'white100' : 'black100',
      }
    }

    return (
      <Stack
        gap={2}
        style={{
          height: 24,
          padding: '4px 4px 4px 8px',
          borderRadius: radius[12],
          background: settings.background,
        }}
      >
        <Text variant="subtext-medium" color={settings.text as any} singleLine>
          {text}
        </Text>
        <Button
          variant="ghost"
          size="small"
          style={{ padding: 0 }}
          tooltip={settings.tooltip}
          onClick={onClick}
        >
          <Icon color={settings.iconColor as any} variant="close" />
        </Button>
      </Stack>
    )
  }

  return (
    <Stack gap={4} style={{ position: 'relative', marginRight: 8 }}>
      {followMe?.following?.color && (
        <span className="bh dq dp h o w jd"
          style={{
            border: `3px solid ${(colors as any)[followMe?.following?.color]}`,}}
        />
      )}
      {following && (
        <NotifyBadge
          text={`Following ${following}`}
          type="following"
          color={(colors as any)[followMe?.following?.color]}
          onClick={() => setFollowMe({ following: { id: null, color: null } })}
        />
      )}
      {Boolean(followers.length) && (
        <NotifyBadge
          text={`Followed by ${followers.length}`}
          type="followed"
          onClick={() => setFollowMe({ followers: [] })}
        />
      )}

      {availableUsers?.slice(0, 2).length ? (
        <Stack gap={0}>
          {availableUsers
            ?.slice(0, 2)
            .map(
              ({ id, email, name, picture, followMe }: any, index: number) => {
                const color =
                  userAvatarColors[
                    followMe?.hash % userAvatarColors.length || 1
                  ]

                return (
                  <UserAvatar
                    key={`${id}-${index}-${email}`}
                    move
                    src={picture}
                    disabled={privateMode}
                    email={email}
                    name={name}
                    id={id}
                    index={index}
                    color={color}
                    zIndex={availableUsers?.length - index}
                    onClick={() => handleFollowMe(id, color)}
                  />
                )
              },
            )}
        </Stack>
      ) : null}
      {availableUsers?.slice(2).length ? (
        <Menu>
          <Menu.Trigger>
            <div className="a9"
              style={{ marginLeft: -4, marginRight: -8 }}
            >
              <Avatar
                label={`+${availableUsers?.length - 2 > 9 ? '9' : availableUsers?.length - 2}`}
                color="neutral100"
              />
            </div>
          </Menu.Trigger>
          <Menu.Items>
            {availableUsers
              ?.slice(2)
              ?.map(({ id, email, name, followMe }: any, index) => {
                const color =
                  userAvatarColors[
                    followMe?.hash % userAvatarColors.length || 1
                  ]

                return (
                  <Menu.Item
                    key={`${id}-${index}-${email}`}
                    onClick={() => handleFollowMe(id, color)}
                  >
                    <Stack direction="row" gap={8}>
                      <Avatar
                        disabled={privateMode}
                        label={email}
                        color={color as any}
                      />
                      <Stack direction="column">
                        <Text>{capitalize(name)}</Text>
                        <Text
                          variant="subtext-regular"
                          color="neutral60"
                          style={{ marginBottom: 4 }}
                        >
                          {email}
                        </Text>
                      </Stack>
                    </Stack>
                  </Menu.Item>
                )
              })}
          </Menu.Items>
        </Menu>
      ) : null}
    </Stack>
  )
}
